import { ArrowDownLeft, ArrowUpRight, CheckCircle2, Clock3, CreditCard, ExternalLink, Plus, ShieldCheck, WalletCards } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { api, post } from '../api';
import { useAuth } from '../App';
import Modal from '../components/Modal';
import PageMotion from '../components/PageMotion';
import { money, shortDate } from '../format';
import { PUBLIC_OFFER_URL, PUBLIC_OFFER_VERSION } from '../legal';
import type { Transaction } from '../types';

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const [balance, setBalance] = useState(user?.balance || 0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [amount, setAmount] = useState(1000);
  const [pending, setPending] = useState(false);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [paid, setPaid] = useState(false);
  const [payment, setPayment] = useState<{
    topup: { id: number; amount: number; fee_amount: number; payable_amount: number; status: string };
    payment_url: string;
    sbp_payment_url: string;
    qr_data_url: string | null;
    fee_percent: number;
    ttl_minutes: number;
  } | null>(null);

  const loadWallet = () => api<{ balance: number; transactions: Transaction[] }>('/wallet').then((result) => {
    setBalance(result.balance);
    setTransactions(result.transactions);
  });

  useEffect(() => { loadWallet(); }, []);
  useEffect(() => {
    if (!payment || paid) return;
    let active = true;
    const check = async () => {
      try {
        const result = await api<{ paid: boolean; balance: number; topup: { status: string } }>(`/wallet/topup/${payment.topup.id}/status`);
        if (!active) return;
        if (result.paid) {
          setBalance(result.balance);
          setPaid(true);
          await Promise.all([loadWallet(), refreshUser()]);
          window.setTimeout(() => {
            setOpen(false);
            setPayment(null);
            setPaid(false);
          }, 2200);
        } else if (['cancelled', 'canceled', 'failed'].includes(result.topup.status)) {
          setError('Счёт отменён или срок оплаты истёк. Создайте новый.');
          setPayment(null);
        }
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : 'Не удалось проверить оплату');
      }
    };
    check();
    const timer = window.setInterval(check, 3500);
    return () => { active = false; window.clearInterval(timer); };
  }, [payment, paid]);

  const topup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setError(''); setPending(true);
    try {
      const result = await post<NonNullable<typeof payment>>('/wallet/topup', {
        amount,
        offer_accepted: offerAccepted,
        offer_version: PUBLIC_OFFER_VERSION
      });
      setPayment(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось создать счёт');
    } finally { setPending(false); }
  };
  const openModal = () => { setOfferAccepted(false); setOpen(true); };
  const closeModal = () => { setOpen(false); setPayment(null); setPaid(false); setError(''); setOfferAccepted(false); };
  const fee = Math.ceil(amount * 0.7) / 100;
  return (
    <PageMotion>
      <header className="page-header"><div><span className="eyebrow">Финансы</span><h1>Баланс</h1><p>Пополнения, покупки и возвраты.</p></div></header>
      <section className="wallet-hero">
        <div><span><WalletCards size={17} /> Доступно</span><h2>{money(balance)}</h2><p>Оплачивайте новые кампании без ожидания.</p></div>
        <button className="light-button" onClick={openModal}><Plus size={18} /> Пополнить баланс</button>
      </section>
      <section className="wallet-security"><ShieldCheck size={21} /><div><b>Безопасная оплата</b><span>Платёж обрабатывается банком, данные карты не хранятся в HOLLA MARKET.</span></div><CreditCard size={23} /></section>
      <section className="content-section transactions-section">
        <div className="section-title"><div><span className="eyebrow">Операции</span><h2>История баланса</h2></div></div>
        {transactions.length ? transactions.map((item) => <div className="transaction-row" key={item.id}><span className={item.amount >= 0 ? 'transaction-in' : 'transaction-out'}>{item.amount >= 0 ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}</span><div><b>{item.title}</b><small>{shortDate(item.created_at)}</small></div><strong className={item.amount >= 0 ? 'positive' : ''}>{item.amount >= 0 ? '+' : ''}{money(item.amount)}</strong></div>) : <div className="empty-inline">Операций пока нет</div>}
      </section>
      <Modal open={open} title={paid ? 'Баланс пополнен' : payment ? 'Оплата по СБП' : 'Пополнить баланс'} eyebrow={paid ? 'Готово' : 'T‑Bank'} onClose={closeModal}>
        {paid ? <div className="success-state payment-success"><span><CheckCircle2 size={29} /></span><h3>Оплата получена</h3><p>Средства уже зачислены на ваш баланс. Окно закроется автоматически.</p></div> : payment ? <div className="payment-stage">
          {payment.qr_data_url && <div className="payment-qr"><img src={payment.qr_data_url} alt="QR-код для оплаты по СБП" /></div>}
          <div className="payment-summary">
            <div><span>На баланс</span><strong>{money(payment.topup.amount)}</strong></div>
            <div><span>Комиссия СБП</span><strong>{money(payment.topup.fee_amount)}</strong></div>
            <div className="payment-total"><span>К оплате</span><strong>{money(payment.topup.payable_amount)}</strong></div>
          </div>
          <a className="primary-button full" href={payment.sbp_payment_url || payment.payment_url} target="_blank" rel="noreferrer">Оплатить по СБП <ExternalLink size={17} /></a>
          <div className="payment-wait"><span className="spinner" /><div><b>Ожидаем оплату</b><small>Проверяем автоматически · счёт действует {payment.ttl_minutes} минут</small></div></div>
          {error && <div className="form-error">{error}</div>}
        </div> : <form className="topup-form" onSubmit={topup}>
          <label>Сумма пополнения<input name="amount" type="number" min="100" max="100000" step="100" value={amount} onChange={(event) => setAmount(Number(event.target.value))} placeholder="1 000 ₽" required autoFocus /></label>
          <div className="amount-presets">{[500, 1000, 3000, 5000].map((preset) => <button type="button" className={amount === preset ? 'active' : ''} key={preset} onClick={() => setAmount(preset)}>{money(preset)}</button>)}</div>
          <div className="topup-preview"><div><span>На баланс</span><strong>{money(amount)}</strong></div><div><span>Комиссия 0,7%</span><strong>{money(fee)}</strong></div><div><span>К оплате</span><strong>{money(amount + fee)}</strong></div></div>
          <label className="legal-consent">
            <input type="checkbox" checked={offerAccepted} onChange={(event) => setOfferAccepted(event.target.checked)} />
            <span>Я принимаю условия <a href={PUBLIC_OFFER_URL} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>Публичной оферты</a> (PDF, редакция {PUBLIC_OFFER_VERSION})</span>
          </label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary-button full" disabled={pending || amount < 100 || !offerAccepted}>{pending ? <span className="spinner" /> : <>Создать счёт <Clock3 size={17} /></>}</button>
          <p className="form-hint">После оплаты баланс обновится автоматически.</p>
        </form>}
      </Modal>
    </PageMotion>
  );
}
